import { ChangeEvent, FocusEvent, forwardRef, InputHTMLAttributes, ReactNode } from 'react'

import { styled, css } from 'styled-components'

import { Field } from 'core/hooks/useForm'
import { error, outline, primary, variables } from 'core/styles'

export type InputVariant = 'outline' | 'underline' | 'none' | 'compact'

export type TextInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'value' | 'onChange' | 'name'> &
  Field<string> & {
    className?: string
    /** The visual style of the input. */
    variant?: InputVariant
    /** A CSS string defining the width of the input. */
    width?: string
    /** A node to insert before the value text */
    prefix?: ReactNode
    /** A node to insert after the value text */
    postfix?: ReactNode
    /** Controls how the value text is formatted and what characters are allowed.  */
    format?: 'string' | 'currency' | 'float' | 'integer'
  }

const amountPattern = /^\d*([.]?\d{0,2})?$/
const integerPattern = /^\d*$/

/**
 * A text input with several variants and display formats. Note that `format` only
 * controls the display of the text the `value` is always handled as a string.
 */
const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      prefix,
      postfix,
      variant,
      width,
      disabled,
      className,
      placeholder,
      onChange,
      onBlur,
      format = 'string',
      errorMessage,
      ...props
    },
    ref,
  ) => {
    const common = {
      disabled,
      onChange: (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        if (
          format === 'string' ||
          (format === 'currency' && amountPattern.test(value)) ||
          (format === 'float' && Number.isFinite(Number(value))) ||
          (format === 'integer' && integerPattern.test(value))
        ) {
          onChange?.(value)
        }
      },
      onBlur: (e: FocusEvent<HTMLInputElement>) => {
        if (format !== 'string' && e.target.value !== '') {
          const value =
            format === 'currency' ? Number(e.target.value).toFixed(2)
            : format === 'float' ? String(Number(e.target.value))
            : Number(e.target.value).toFixed(0)

          if (value !== e.target.value) {
            onChange?.(value)
          }
        }

        return onBlur?.(e)
      },
      placeholder:
        placeholder ??
        (format === 'currency' ? '0.00'
        : format === 'float' ? '0.0'
        : format === 'integer' ? '0'
        : undefined),
    }

    const _prefix = prefix ?? (format === 'currency' ? '$' : undefined)

    return (
      <>
        {_prefix || postfix ?
          <Wrapper
            $disabled={disabled}
            $errorMessage={!!errorMessage}
            $variant={variant}
            $width={width}
            className={className}
          >
            <Fixture>{_prefix}</Fixture>
            <input ref={ref} {...props} {...common} />
            <Fixture>{postfix}</Fixture>
          </Wrapper>
        : <StyledInput
            $errorMessage={!!errorMessage}
            $variant={variant}
            $width={width}
            className={className}
            ref={ref}
            {...props}
            {...common}
          />
        }
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </>
    )
  },
)

TextInput.displayName = 'TextInput'

export default TextInput

const Fixture = styled.span`
  color: ${variables.colorBlack60};
`

export const common = css<{ $width?: string; $variant?: InputVariant; $disabled?: boolean; $errorMessage?: boolean }>`
  box-sizing: border-box;
  border-width: 0 0 1px;
  border-style: solid;
  border-color: transparent;
  width: ${(p) => (p.$width ? p.$width : 'auto')};

  ${(p) =>
    p.$disabled &&
    `
    // Matching the ReactSelect default styles (?) here for consistency
    color: #999999;
    background-color: #f2f2f2;
    border-color: #e6e6e6;
  `}

  ${(p) =>
    (p.$variant === 'underline' || p.$variant === 'outline') &&
    `
      border-color: ${variables.colorBlack20};

      &:hover {
        border-color: ${variables.colorBlack30};
      }
  `}

  ${(p) =>
    p.$variant === 'compact' &&
    `
      border-color: ${outline};

      &:hover {
        border-color: ${outline};
      }
  `}

  ${(p) =>
    p.$errorMessage &&
    `
      border-color: ${error} !important;
    `}

  ${(p) =>
    p.$variant === 'outline' &&
    `
      padding: 8px;
      border-width: 1px;
      border-radius: 4px;
    `}

  ${(p) =>
    p.$variant === 'compact' &&
    `
      padding: 0 8px;
      border-width: 1px;
      border-radius: 8px;
      height: 32px;
      font-size: 14px;
      font-weight: 500;
    `}

  &:focus-within:not([readonly]) {
    outline: none;
    border-color: ${(p) => (p.$variant === 'compact' ? primary : variables.colorBlueSecondary)};
  }
`

export const commonInput = css`
  border: none;
  background-color: transparent;
  color: inherit;
  font-family: inherit;
  font-size: 14px;

  ::placeholder {
    color: ${variables.colorBlack50};
  }
`

export const Wrapper = styled.div<{
  $width?: string
  $variant?: InputVariant
  $disabled?: boolean
  $errorMessage?: boolean
}>`
  ${common}
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  column-gap: 2px;
  align-items: center;

  input {
    ${commonInput}
    &:focus-within {
      outline: none;
      border: none;
    }
  }
`

const StyledInput = styled.input<{
  $width?: string
  $variant?: InputVariant
  $disabled?: boolean
  $errorMessage?: boolean
}>`
  ${commonInput}
  ${common}
`

const ErrorMessage = styled.div`
  margin-top: 4px;
  color: ${error};
  font-size: 12px;
`
